import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  AlertTriangle,
  Hospital,
  Pill,
  User2,
  Users,
  ExternalLink,
  Star,
  Footprints,
} from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import WhatsAppFab from "../components/site/WhatsAppFab";
import AvailabilityBar from "../components/site/AvailabilityBar";
import { useT } from "../i18n/LanguageContext";

const DISTANCES = [
  { name: "Coviran", val: "80m", foot: true, icon: "🛒" },
  { name: "Padaria", val: "150m", foot: true, icon: "🥖" },
  { name: "Bar-Clube MG", val: "200m", foot: true, icon: "⚽" },
  { name: "Adega Vila Lisa", val: "400m", foot: true, icon: "🍷" },
  { name: "Estação CP", val: "500m", foot: true, icon: "🚂" },
  { name: "Portimão Centro", val: "10 min", foot: false, icon: "🏙️" },
  { name: "Praia de Alvor", val: "12 min", foot: false, icon: "🏖️" },
  { name: "Praia Três Irmãos", val: "15 min", foot: false, icon: "🌊" },
  { name: "Meia Praia, Lagos", val: "18 min", foot: false, icon: "🌅" },
  { name: "Lagos", val: "20 min", foot: false, icon: "🏛️" },
  { name: "Aeroporto Faro", val: "~50 min", foot: false, icon: "✈️" },
];

export default function Sobre() {
  const { t, lang } = useT();
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("visible"); io.unobserve(e.target); } }),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [lang]);

  return (
    <main data-testid="sobre-page" className="overflow-x-hidden">
      <AvailabilityBar />
      <Header />

      {/* HERO */}
      <section className="bg-noite text-[#faf6ee] py-20 md:py-28 relative overflow-hidden" data-testid="sobre-hero">
        <div className="absolute -top-32 left-1/3 w-[30rem] h-[30rem] rounded-full bg-[#e3b25b]/8 blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 relative reveal">
          <p className="text-[11px] tracking-[0.22em] uppercase text-[#e3b25b]/90">
            <Link to="/" className="hover:underline">{t.about.hero.eyebrow.split("›")[0].trim()}</Link>
            <span className="mx-2">›</span>
            {t.about.hero.eyebrow.split("›")[1].trim()}
          </p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.05]">
            {t.about.hero.title1}
            <br />
            <em className="text-[#e3b25b]">{t.about.hero.title2}</em>
          </h1>
          <p className="mt-6 text-lg text-[#faf6ee]/80 max-w-2xl leading-relaxed">
            {t.about.hero.subtitle}
          </p>
        </div>
        <div className="azulejo-band mt-16" aria-hidden="true" />
      </section>

      {/* OUR STORY */}
      <section className="py-20 md:py-28 bg-paper" data-testid="sobre-story">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
          <div className="reveal relative">
            <div className="rounded-3xl overflow-hidden ring-1 ring-[#0e1a3f]/10 aspect-[4/5] bg-[#d6e2f5] relative">
              <img
                src="/assets/logo.jpg"
                alt="MexiHome5"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 text-[10px] uppercase tracking-widest text-white bg-black/40 backdrop-blur px-3 py-1 rounded-full">
                MexiHome5 · Mexilhoeira Grande
              </div>
            </div>
            <div className="absolute -bottom-4 -left-4 sm:-left-6 bg-[#0e1a3f] text-[#faf6ee] px-5 py-4 rounded-2xl shadow-2xl flex items-center gap-3">
              <Star size={20} className="fill-[#e3b25b] text-[#e3b25b]" />
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#e3b25b]">5</p>
                <p className="text-sm font-display">{t.about.story.license}</p>
              </div>
            </div>
          </div>

          <div className="reveal">
            <p className="section-label">{t.about.story.eyebrow}</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[#0e1a3f] leading-tight">
              {t.about.story.welcome} <em className="text-[#1e3a8a]">MexiHome5</em>
            </h2>
            <div className="mt-6 space-y-4 text-[#0e1a3f]/80 leading-relaxed">
              <p>{t.about.story.body1}</p>
              <p>{t.about.story.body2}</p>
              <p>{t.about.story.body3}</p>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {t.about.story.metrics.map((m) => (
                <div key={m.l} className="bg-white rounded-2xl p-4 ring-1 ring-[#0e1a3f]/10 text-center">
                  <p className="font-display text-3xl text-[#1e3a8a]">{m.v}</p>
                  <p className="text-[10px] uppercase tracking-widest text-[#0e1a3f]/65 mt-1">{m.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOSTS */}
      <section className="py-20 md:py-28 bg-noite text-[#faf6ee] relative overflow-hidden" data-testid="sobre-hosts">
        <div className="absolute -top-40 right-0 w-[28rem] h-[28rem] rounded-full bg-[#c89033]/15 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="reveal text-center max-w-2xl mx-auto">
            <p className="section-label justify-center">{t.about.hosts.eyebrow}</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4">
              {t.about.hosts.title1} <em className="text-[#e3b25b]">{t.about.hosts.title2}</em>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {/* Carlos */}
            <a href="tel:+351965209781" className="reveal card-lift bg-[#1a2c5c]/55 ring-1 ring-white/10 p-6 rounded-2xl block" data-testid="sobre-host-carlos">
              <div className="w-12 h-12 rounded-xl bg-[#c89033]/25 text-[#e3b25b] flex items-center justify-center"><User2 size={22} /></div>
              <p className="text-[10px] uppercase tracking-widest text-[#e3b25b] mt-4">{t.about.hosts.cards.carlos.tag}</p>
              <p className="font-display text-xl mt-1">Carlos Simões</p>
              <p className="text-[#e3b25b] font-display text-lg mt-1">+351 965 209 781</p>
              <p className="text-sm text-[#faf6ee]/70 mt-3 leading-relaxed">{t.about.hosts.cards.carlos.desc}</p>
            </a>
            {/* Rute */}
            <a href="tel:+351965488960" className="reveal card-lift bg-[#1a2c5c]/55 ring-1 ring-white/10 p-6 rounded-2xl block" data-testid="sobre-host-rute">
              <div className="w-12 h-12 rounded-xl bg-[#c89033]/25 text-[#e3b25b] flex items-center justify-center"><Users size={22} /></div>
              <p className="text-[10px] uppercase tracking-widest text-[#e3b25b] mt-4">{t.about.hosts.cards.rute.tag}</p>
              <p className="font-display text-xl mt-1">Rute Gonçalves</p>
              <p className="text-[#e3b25b] font-display text-lg mt-1">+351 965 488 960</p>
              <p className="text-sm text-[#faf6ee]/70 mt-3 leading-relaxed">{t.about.hosts.cards.rute.desc}</p>
            </a>
            {/* Email */}
            <a href="mailto:mexihome5@hotmail.com" className="reveal card-lift bg-[#1a2c5c]/55 ring-1 ring-white/10 p-6 rounded-2xl block" data-testid="sobre-host-email">
              <div className="w-12 h-12 rounded-xl bg-[#c89033]/25 text-[#e3b25b] flex items-center justify-center"><Mail size={22} /></div>
              <p className="text-[10px] uppercase tracking-widest text-[#e3b25b] mt-4">{t.about.hosts.cards.email.tag}</p>
              <p className="font-display text-lg mt-1 break-all">mexihome5@hotmail.com</p>
              <p className="text-[10px] text-[#e3b25b] mt-1 uppercase tracking-widest">{t.about.hosts.cards.email.cta} →</p>
              <p className="text-sm text-[#faf6ee]/70 mt-3 leading-relaxed">{t.about.hosts.cards.email.desc}</p>
            </a>
            {/* SOS */}
            <a href="tel:112" className="reveal card-lift bg-[#1a2c5c]/55 ring-1 ring-white/10 p-6 rounded-2xl block" data-testid="sobre-host-sos">
              <div className="w-12 h-12 rounded-xl bg-[#c0492c]/30 text-[#ff8b6a] flex items-center justify-center"><AlertTriangle size={22} /></div>
              <p className="text-[10px] uppercase tracking-widest text-[#ff8b6a] mt-4">{t.about.hosts.cards.sos.tag}</p>
              <p className="font-display text-3xl mt-1">112</p>
              <p className="text-[10px] text-[#ff8b6a] mt-1 uppercase tracking-widest">{t.about.hosts.cards.sos.cta} →</p>
              <p className="text-sm text-[#faf6ee]/70 mt-3 leading-relaxed">{t.about.hosts.cards.sos.desc}</p>
            </a>
            {/* Hospital */}
            <a href="tel:+351282450300" className="reveal card-lift bg-[#1a2c5c]/55 ring-1 ring-white/10 p-6 rounded-2xl block" data-testid="sobre-host-hospital">
              <div className="w-12 h-12 rounded-xl bg-[#c89033]/25 text-[#e3b25b] flex items-center justify-center"><Hospital size={22} /></div>
              <p className="text-[10px] uppercase tracking-widest text-[#e3b25b] mt-4">{t.about.hosts.cards.hospital.tag}</p>
              <p className="font-display text-xl mt-1">282 450 300</p>
              <p className="text-[10px] text-[#e3b25b] mt-1 uppercase tracking-widest">{t.about.hosts.cards.hospital.cta} →</p>
              <p className="text-sm text-[#faf6ee]/70 mt-3 leading-relaxed">{t.about.hosts.cards.hospital.desc}</p>
            </a>
            {/* Farmácia */}
            <div className="reveal card-lift bg-[#1a2c5c]/55 ring-1 ring-white/10 p-6 rounded-2xl" data-testid="sobre-host-farmacia">
              <div className="w-12 h-12 rounded-xl bg-[#c89033]/25 text-[#e3b25b] flex items-center justify-center"><Pill size={22} /></div>
              <p className="text-[10px] uppercase tracking-widest text-[#e3b25b] mt-4">{t.about.hosts.cards.farmacia.tag}</p>
              <p className="font-display text-xl mt-1">Mexilhoeira Grande</p>
              <p className="text-sm text-[#faf6ee]/70 mt-3 leading-relaxed">{t.about.hosts.cards.farmacia.desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* MAP + ADDRESS + DISTANCES */}
      <section className="py-20 md:py-28 bg-paper" data-testid="sobre-map">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal max-w-3xl">
            <p className="section-label">{t.about.map.eyebrow}</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[#0e1a3f] leading-tight">
              {t.about.map.title1} <em className="text-[#1e3a8a]">{t.about.map.title2}</em>
            </h2>
          </div>

          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mt-10">
            <div className="reveal rounded-3xl overflow-hidden ring-1 ring-[#0e1a3f]/10 bg-white">
              <iframe
                title="MexiHome5 location"
                src="https://www.google.com/maps?q=Rua+Valeriano+Jo%C3%A3o+da+Gl%C3%B3ria+5+Mexilhoeira+Grande&output=embed"
                className="w-full h-[420px] sm:h-[520px] block border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="bg-[#0e1a3f] text-[#faf6ee]/85 px-5 py-3 text-xs flex items-center gap-2 justify-between flex-wrap">
                <span className="flex items-center gap-2"><MapPin size={14} className="text-[#e3b25b]" /> MexiHome5 · Rua Valeriano João da Glória, Nº 5 · 8500-132 Mexilhoeira Grande</span>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=Rua+Valeriano+Jo%C3%A3o+da+Gl%C3%B3ria+5+Mexilhoeira+Grande"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#e3b25b] hover:underline flex items-center gap-1"
                  data-testid="sobre-map-open"
                >
                  {t.about.map.openMap} <ExternalLink size={12} />
                </a>
              </div>
            </div>

            <div className="space-y-6">
              {/* Address card */}
              <div className="reveal bg-white rounded-2xl p-6 ring-1 ring-[#0e1a3f]/10">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#c89033]">
                  <MapPin size={14} /> {t.about.map.addressLabel}
                </div>
                <p className="font-display text-xl text-[#0e1a3f] mt-2">Rua Valeriano João da Glória, Nº 5</p>
                <p className="text-sm text-[#0e1a3f]/75 mt-1">Mexilhoeira Grande, 8500-132</p>
                <p className="text-sm text-[#0e1a3f]/75">Portimão · Algarve · Portugal</p>
              </div>

              {/* Distances card */}
              <div className="reveal bg-white rounded-2xl p-6 ring-1 ring-[#0e1a3f]/10">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#c89033]">
                  <Footprints size={14} /> {t.about.map.distancesLabel}
                </div>
                <ul className="mt-3 space-y-2 max-h-[420px] overflow-y-auto pr-2">
                  {DISTANCES.map((d) => (
                    <li
                      key={d.name}
                      className="flex items-center justify-between gap-3 py-2 border-b border-[#0e1a3f]/8 last:border-0"
                      data-testid={`sobre-dist-${d.name.replace(/\s/g, "-").toLowerCase()}`}
                    >
                      <span className="text-sm text-[#0e1a3f] flex items-center gap-2">
                        <span aria-hidden="true">{d.icon}</span> {d.name}
                      </span>
                      <span className="text-xs font-semibold text-[#1e3a8a]">
                        {d.val}{d.foot && <span className="text-[#0e1a3f]/55 font-normal"> · {t.about.map.walk}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </main>
  );
}
